import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { compose } from 'redux';
import { change, Field, reduxForm } from 'redux-form';
import { required } from 'redux-form-validators';
import PropTypes from 'prop-types';

import { RadioButton } from 'common/components';

import { ReduxTextField, ValueSwitcherField, ReduxSelectField } from '../../form-fields';

import { NewDefaultButton } from '../..';

import { wsConfigureMt, wsSkipMtStep, wsSetStep, wsGetMtData, wsSetIsReenteringMtData } from '../../../_redux';

import { staticStyles } from './style';

const mt4EncodingOptions = [
  { label: 'Windows-1251 (Cyrillic)', value: 'cp1251' },
  { label: 'Windows-1250 (Eastern Europe)', value: 'cp1250' },
  { label: 'GBK (Unified Chinese)', value: 'gbk' },
];

const validate = values => {
  const errors = {};

  if (values.mtPassword !== values.mtPasswordConfirm)
    errors.mtPasswordConfirm = <FormattedMessage id="form.errors.passwordRepeat" />;

  return errors;
};

const ConfigureMtForm = ({
  pristine,
  submitting,
  invalid,
  error,
  handleSubmit,
  dispatch,
  mtIsValid,
  isSkippedMtStep,
  isSkippingMtStep,
  changeFieldValue,
  isReenteringMTData,
  wsGetMtData,
  acceptedMtDataIsLoading,
  acceptedMtData,
}) => {
  const [isMtFormVisible, setIsMtFormVisible] = useState(mtIsValid && !isSkippedMtStep);
  const [selectedPlatformType, setSelectedPlatformType] = useState(mtIsValid && !isSkippedMtStep ? 'metatrader' : '');
  const [checkedValue, setCheckedValue] = useState('mt4');

  const [passwordFieldType, setPasswordFieldType] = useState('password');

  useEffect(() => {
    if (mtIsValid && !isSkippedMtStep) {
      wsGetMtData();
    }
  }, []);

  const wsSetIsReenteringMtDataFunc = () => {
    if (mtIsValid && !isReenteringMTData) {
      dispatch(wsSetIsReenteringMtData(true));
    }
  };

  useEffect(() => {
    if (!acceptedMtDataIsLoading && acceptedMtData && acceptedMtData.mtType !== 'SKIPPED') {
      Object.keys(acceptedMtData).forEach(key => {
        changeFieldValue(key, acceptedMtData[key]);
      });
    }
  }, [acceptedMtDataIsLoading, acceptedMtData]);
  const actionTooltipId = passwordFieldType === 'password' ? 'showPassword' : 'hidePassword';
  const actionClassName = passwordFieldType === 'password' ? 'show' : 'hide';

  const showPassword = () => {
    if (passwordFieldType === 'password') setPasswordFieldType('text');
    else setPasswordFieldType('password');
  };

  const handlePreviousStepClick = () => dispatch(wsSetStep(3));
  const handlePlatformTypeBackClick = () => setIsMtFormVisible(false);
  const handleNextStepClick = () => dispatch(wsSetStep(5));

  const handleStepSkip = () => {
    dispatch(wsSkipMtStep.request());
  };

  const handlePlatformTypeContinue = () => {
    if (selectedPlatformType === 'metatrader') {
      setIsMtFormVisible(true);
      return;
    }

    handleStepSkip();
  };

  const submit = handleSubmit(formValues => wsConfigureMt(formValues, dispatch));

  const handleCheckedValueChange = (e, value) => {
    setCheckedValue(value);

    if (value === 'mt5') changeFieldValue('mtServerEncoding', '');
  };

  if (!isMtFormVisible) {
    return (
      <div className="ConfigureMtForm ConfigureMtForm--platform-type">
        <FormattedMessage id="welcomeSetupTradingPlatformTypeQuestion">
          {txt => <h2 className="ConfigureMtForm__platform-type-title">{txt}</h2>}
        </FormattedMessage>
        <div className="ConfigureMtForm__platform-types">
          <RadioButton
            textId="welcomeSetupMetaTraderPlatformType"
            name="welcomeSetupTradingPlatformType"
            value="metatrader"
            onChange={() => setSelectedPlatformType('metatrader')}
            checked={selectedPlatformType === 'metatrader'}
            disabled={acceptedMtDataIsLoading || isSkippingMtStep || submitting}
          />
          <RadioButton
            textId="welcomeSetupOtherPlatformType"
            name="welcomeSetupTradingPlatformType"
            value="other"
            onChange={() => setSelectedPlatformType('other')}
            checked={selectedPlatformType === 'other'}
            disabled={acceptedMtDataIsLoading || isSkippingMtStep || submitting}
          />
        </div>
        <div className="ConfigureMtForm__nav-buttons-bottom">
          <NewDefaultButton
            textId="justBack"
            type="button"
            onClick={handlePreviousStepClick}
            disabled={acceptedMtDataIsLoading || isSkippingMtStep || submitting}
            variant="ghost"
            iconVariant="previous"
            isUpperCase
          />
          <NewDefaultButton
            textId="clientContinue"
            type="button"
            onClick={handlePlatformTypeContinue}
            disabled={!selectedPlatformType || acceptedMtDataIsLoading || isSkippingMtStep || submitting}
            variant="primary"
            wide
            isUpperCase
          />
        </div>
        {error && <strong className="formError">{error}</strong>}

        <style jsx global>
          {staticStyles}
        </style>
      </div>
    );
  }

  return (
    <form className="ConfigureMtForm" onSubmit={submit}>
      <ValueSwitcherField
        leftTextId="justMT4"
        rightTextId="justMT5"
        leftValue="mt4"
        rightValue="mt5"
        checkedValue={checkedValue}
        name="mtType"
        disabled={acceptedMtDataIsLoading || submitting}
        validate={[required()]}
        onChange={(...args) => {
          handleCheckedValueChange(...args);
          wsSetIsReenteringMtDataFunc();
        }}
      />
      {checkedValue === 'mt4' && (
        <Field
          component={ReduxSelectField}
          name="mtServerEncoding"
          textId="welcomeSetupMtServerEncoding"
          options={mt4EncodingOptions}
          disabled={acceptedMtDataIsLoading || submitting}
          validate={[required()]}
          onChange={() => {
            wsSetIsReenteringMtDataFunc();
          }}
          maxMenuHeight={140}
        />
      )}
      <Field
        component={ReduxTextField}
        type="text"
        name="mtDomain"
        placeholder="192.168.0.1"
        textId="welcomeSetupMtServerAddress"
        validate={[required()]}
        onChange={() => {
          wsSetIsReenteringMtDataFunc();
        }}
        disabled={acceptedMtDataIsLoading || submitting}
      />
      <Field
        component={ReduxTextField}
        type="number"
        name="mtPort"
        placeholder="443"
        textId="welcomeSetupMtPort"
        onChange={() => {
          wsSetIsReenteringMtDataFunc();
        }}
        validate={[required()]}
        disabled={acceptedMtDataIsLoading || submitting}
      />
      <Field
        component={ReduxTextField}
        type="number"
        name="mtLogin"
        autoComplete="off"
        tooltipId="welcomeSetupLoginToYourMetatraderAccount"
        tooltipTextId="welcomeSetupLoginToYourMetatraderAccount"
        textId="welcomeSetupMtLogin"
        validate={[required()]}
        onChange={() => {
          wsSetIsReenteringMtDataFunc();
        }}
        disabled={acceptedMtDataIsLoading || submitting}
      />
      <Field
        component={ReduxTextField}
        type={passwordFieldType}
        onChange={() => {
          wsSetIsReenteringMtDataFunc();
        }}
        tooltipId="welcomeSetupPasswordToYourMetatraderAccount"
        tooltipTextId="welcomeSetupPasswordToYourMetatraderAccount"
        name="mtPassword"
        textId="authPassword"
        autoComplete="new-password"
        validate={[required()]}
        disabled={acceptedMtDataIsLoading || submitting}
        action={showPassword}
        actionTooltipId={actionTooltipId}
        actionClassName={actionClassName}
      />
      {isReenteringMTData && (
        <span className="ConfigureMtForm__YouHaveEnteredNewData">
          <FormattedMessage id="welcomeSetupMtYouveEnteredNewData" />
        </span>
      )}
      <div className="ConfigureMtForm__nav-buttons">
        <NewDefaultButton
          textId="welcomeSetupConnectToMt"
          type="submit"
          disabled={invalid || pristine || submitting}
          variant="secondary"
          wide
          isUpperCase
        />
        <div className="ConfigureMtForm__nav-buttons-bottom">
          <NewDefaultButton
            textId="justBack"
            type="button"
            onClick={handlePlatformTypeBackClick}
            disabled={submitting}
            variant="ghost"
            iconVariant="previous"
            isUpperCase
          />
          <NewDefaultButton
            textId="justNextStep"
            type="button"
            onClick={handleNextStepClick}
            disabled={!mtIsValid || isReenteringMTData}
            variant="primary"
            wide
            isUpperCase
          />
        </div>
      </div>
      {error && <strong className="formError">{error}</strong>}

      <style jsx global>
        {staticStyles}
      </style>
    </form>
  );
};

ConfigureMtForm.propTypes = {
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  changeFieldValue: PropTypes.func.isRequired,
  isReenteringMTData: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  mtIsValid: PropTypes.bool.isRequired,
  isSkippedMtStep: PropTypes.bool.isRequired,
  isSkippingMtStep: PropTypes.bool.isRequired,
  wsGetMtData: PropTypes.func.isRequired,
  acceptedMtDataIsLoading: PropTypes.bool.isRequired,
  acceptedMtData: PropTypes.object,
};

ConfigureMtForm.defaultProps = {
  error: '',
  acceptedMtData: {},
};

export default compose(
  connect(
    state => ({
      mtIsValid: state.welcomeSetup.mtIsValid,
      isSkippedMtStep: state.welcomeSetup.isSkippedMtStep,
      isSkippingMtStep: state.welcomeSetup.isSkippingMtStep,
      acceptedMtData: state.welcomeSetup.acceptedMtData,
      acceptedMtDataIsLoading: state.welcomeSetup.acceptedMtDataIsLoading,
    }),
    {
      changeFieldValue: (field, value) => change('wsConfigureMtForm', field, value),
      wsSetStep,
      wsSetIsReenteringMtData,
      wsGetMtData: () => wsGetMtData.request(),
    }
  ),
  reduxForm({
    form: 'wsConfigureMtForm',
    validate,
    onSubmitSuccess: (result, dispatch, { wsSetStep }) => {
      wsSetStep(5);
      wsSetIsReenteringMtData(false);
    },
    initialValues: {
      mtType: 'mt4',
    },
  })
)(ConfigureMtForm);
export { ConfigureMtForm };
